import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type Step = { id: string; label: string; done?: boolean };

/**
 * Ordered progress through steps. `onSelect` makes steps buttons (wizard / section nav);
 * without it the stepper is read-only. The current step has `aria-current="step"`.
 */
export function Stepper({
  label,
  steps,
  current,
  onSelect,
  orientation = "horizontal",
  className,
}: {
  label: string;
  steps: Step[];
  current: string;
  onSelect?: (id: string) => void;
  orientation?: "horizontal" | "vertical";
  className?: string;
}) {
  const currentIndex = steps.findIndex((s) => s.id === current);
  const vertical = orientation === "vertical";
  return (
    <ol aria-label={label} className={cn("flex", vertical ? "flex-col gap-1" : "w-full items-start gap-2", className)}>
      {steps.map((step, i) => {
        const isCurrent = step.id === current;
        const done = step.done ?? i < currentIndex;
        const marker = (
          <span
            aria-hidden="true"
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold",
              done && "border-secondary bg-secondary text-secondary-fg",
              isCurrent && !done && "border-brand bg-surface text-heading",
              !done && !isCurrent && "border-border-input bg-surface text-fg-muted",
            )}
          >
            {done ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
          </span>
        );
        const text = (
          <span className={cn("text-left text-sm", isCurrent ? "font-semibold text-heading" : "text-fg-muted")}>
            {step.label}
          </span>
        );
        const inner = (
          <span className={cn("flex items-center gap-3", !vertical && "flex-col gap-2 text-center sm:flex-row sm:text-left")}>
            {marker}
            {text}
          </span>
        );
        return (
          <li key={step.id} aria-current={isCurrent ? "step" : undefined} className={cn(!vertical && "flex-1")}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                className={cn(
                  "flex min-h-11 w-full cursor-pointer items-center rounded-md px-2 py-1 transition-colors duration-fast hover:bg-secondary-subtle",
                  isCurrent && vertical && "bg-secondary-subtle",
                )}
              >
                {inner}
              </button>
            ) : (
              <span className="flex min-h-11 items-center px-2 py-1">{inner}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
